import { jsx, jsxs } from 'react/jsx-runtime';
import { useResourceAudioDataContextSafe } from '../../../../../resource-detail/components/context/resourceDataContext.js';
import { ColorLabel } from '../../components/ColorLabel.js';
import { containerPreviewCompact } from '../cssPreviewContainer.js';
import { cssAspectRatio } from '../sharedCss.js';
import { isResourceDetailAudio } from '../../../../utils/resourceDetailGuards.js';
import { styled, css } from 'styled-components';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Flex } from '@fabric-app/ui/components/Flex';
import { Image } from '@fabric-app/ui/components/Image';
import { ResourceGridViewLoadingIndicator } from '../components/ResourceGridViewLoadingIndicator.js';
import { useResourceListPreferencesContextUnsafe } from '../../../ResourceList/contexts/resourceListContextPreferences.js';
import { IconCard30pxKindAudio } from '@fabric-app/icons/svg';
import { renderCssBasedOnColorScheme } from '@fabric-app/ui/theme/renderCssBasedOnColorScheme';
import { PreviewIconContainer } from '../components/PreviewIconContainer.js';
import { formatDuration } from '@fabric-app/utils/number';
import audioPlaceholder from './assets/audio-placeholder.jpg.js';
import { cssLineClamp } from '@fabric-app/ui/constants/styled-utils';
import { useMemo } from 'react';

const ResourcePreviewAudio = () => {
  const { resource, placeholderName } = useResourceAudioDataContextSafe();
  const pref = useResourceListPreferencesContextUnsafe();
  const isAutoHeight = pref?.viewMode === "Masonry";
  const waveImage = useMemo(() => {
    if (resource.thumbnail?.md) {
      return {
        src: resource.thumbnail?.md,
        isPlaceholder: false
      };
    }
    {
      return {
        src: audioPlaceholder,
        isPlaceholder: true
      };
    }
  }, [
    resource.thumbnail?.md
  ]);
  return /* @__PURE__ */ jsx(Wrapper, {
    isAutoHeight,
    children: /* @__PURE__ */ jsxs(ThumbnailWrapper, {
      style: isAutoHeight ? {
        width: "100%"
      } : void 0,
      children: [
        /* @__PURE__ */ jsx(ColorLabel, {
          absPosition: "inside-top-left"
        }),
        /* @__PURE__ */ jsx(ResourceGridViewLoadingIndicator, {
          resource
        }),
        isResourceDetailAudio(resource) && /* @__PURE__ */ jsx(PreviewIconContainer, {
          style: {
            color: `rgba(${cssVar["color-text-primary-rgb"]}, 0.45)`
          },
          children: /* @__PURE__ */ jsx(IconCard30pxKindAudio, {})
        }),
        /* @__PURE__ */ jsx(ImageContainerInner, {
          children: /* @__PURE__ */ jsx(ThumbnailImage, {
            "data-preview-content-wrapper": "stored_file_thumbnail",
            src: waveImage.src,
            alt: resource.name || placeholderName,
            isPlaceholder: waveImage.isPlaceholder
          })
        }),
        /* @__PURE__ */ jsx(SongTitleContainer, {
          children: /* @__PURE__ */ jsxs(SongTitle, {
            children: [
              /* @__PURE__ */ jsx(SongTitle, {
                children: [
                  resource.name,
                  resource.extension
                ].filter(Boolean).join(".")
              }),
              resource.data.attributes?.audio?.duration && /* @__PURE__ */ jsx(Duration, {
                children: formatDuration(resource.data.attributes?.audio.duration)
              })
            ]
          })
        })
      ]
    })
  });
};
const SongTitle = styled.div.withConfig({
  displayName: "ResourcePreviewAudio__SongTitle",
  componentId: "sc-bc04275d-0"
})([
  `font-size:0.875rem;font-weight:600;line-height:1rem;`,
  ``
], cssLineClamp(2));
const SongTitleContainer = styled.div.withConfig({
  displayName: "ResourcePreviewAudio__SongTitleContainer",
  componentId: "sc-bc04275d-1"
})([
  `color:`,
  `;display:none;right:12px;margin-top:auto;flex-grow:0;`,
  `{display:block;}`
], cssVar["color-text-tertiary"], containerPreviewCompact);
const Duration = styled.div.withConfig({
  displayName: "ResourcePreviewAudio__Duration",
  componentId: "sc-bc04275d-2"
})([
  `font-size:0.75rem;font-weight:500;opacity:0.8;margin-top:3px;`
]);
const ThumbnailImage = styled(Image).withConfig({
  displayName: "ResourcePreviewAudio__ThumbnailImage",
  componentId: "sc-bc04275d-3"
})([
  `width:100%;object-fit:cover;display:block;aspect-ratio:144 / 40;`,
  ` `,
  ` transition:none;`,
  `{width:100%;border:none;border-radius:0;}`
], ({ isPlaceholder }) => isPlaceholder ? css([
  `opacity:0.15 !important;`
]) : css([
  `opacity:0.33 !important;`
]), renderCssBasedOnColorScheme({
  light: css([
    ``
  ]),
  dark: css([
    `filter:invert(1);mix-blend-mode:lighten;`
  ])
}), containerPreviewCompact);
const ImageContainerInner = styled.div.withConfig({
  displayName: "ResourcePreviewAudio__ImageContainerInner",
  componentId: "sc-bc04275d-4"
})([
  `display:flex;flex-direction:column;justify-content:center;align-items:center;flex-grow:1;`
]);
const Wrapper = styled.div.withConfig({
  displayName: "ResourcePreviewAudio__Wrapper",
  componentId: "sc-bc04275d-5"
})([
  `width:100%;height:100%;display:flex;flex-direction:column;gap:1rem;flex-grow:1;justify-content:center;align-items:center;position:relative;`,
  ` `,
  `{svg[data-file-icon]{height:60px !important;width:90px !important;}}`
], (p) => p.isAutoHeight ? css([
  `width:100%;`
]) : cssAspectRatio, containerPreviewCompact);
const ThumbnailWrapper = styled(Flex).attrs({
  alignItems: "center",
  direction: "column"
}).withConfig({
  displayName: "ResourcePreviewAudio__ThumbnailWrapper",
  componentId: "sc-bc04275d-6"
})([
  `position:relative;max-height:100%;max-width:100%;border-radius:var(--preview-content-border-radius,1rem);background-color:`,
  `;border:var(--preview-border-width,2px) solid var(--preview-border-color,`,
  `);overflow:hidden;padding:0.75rem;width:260px;height:240px;justify-content:center;align-items:flex-start;`,
  `{width:100%;height:100%;border:none;gap:10px;padding-top:50px;align-items:flex-start;}`
], cssVar["color-bg-primary"], cssVar["color-border-secondary"], containerPreviewCompact);

export { ResourcePreviewAudio };
//# sourceMappingURL=ResourcePreviewAudio.js.map
